import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Avatar,
  Box,
  Typography,
  Grid,
} from '@material-ui/core';
import { Block, CheckCircleOutline, Security, Timer } from '@material-ui/icons';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { IImpersonationDashboard, IImpersonationRequest } from '../../../../Api/AptorApi';
import { useAptorApi } from '../../../../Api';
import { useStyles } from './ImpersonationRequestsWidget.styles';

const emptyDashboard: IImpersonationDashboard = { receivedRequests: [] };

export const ImpersonationRequestsWidget = () => {
  const classes = useStyles();
  const { api, abortController } = useAptorApi();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [dashboard, setDashboard] = useState<IImpersonationDashboard>(emptyDashboard);
  const [respondingId, setRespondingId] = useState<string>();

  const load = useCallback(async () => {
    const result = await api.getImpersonationDashboard();
    if (!abortController.current.signal.aborted) {
      setDashboard({ receivedRequests: result.receivedRequests || [] });
    }
  }, [api, abortController]);

  useEffect(() => {
    load();
  }, [load]);

  const respond = async (request: IImpersonationRequest, accept: boolean) => {
    setRespondingId(request.id);
    try {
      if (accept) {
        await api.acceptImpersonationRequest(request.id);
      } else {
        await api.rejectImpersonationRequest(request.id);
      }
      enqueueSnackbar(
        formatMessage({
          id: accept ? 'dashboard.impersonation.accepted' : 'dashboard.impersonation.rejected',
        }),
        { variant: accept ? 'success' : 'info' },
      );
      await load();
    } finally {
      setRespondingId(undefined);
    }
  };

  const received = dashboard.receivedRequests.filter((request) => request.status === 'pending');

  if (received.length === 0) {
    return null;
  }

  return (
    <Grid item>
      <Card>
        <CardHeader
          avatar={<Avatar style={{ color: '#cc092f', backgroundColor: 'rgba(204, 9, 47, .09)' }}><Security /></Avatar>}
          title={<Typography variant="h3">{formatMessage({ id: 'form.impersonation' })}</Typography>}
        />
        {received.map((request, index) => (
          <React.Fragment key={request.id}>
            {index > 0 && <Divider />}
            <CardContent className={classes.requestContent}>
              <Box className={classes.titleRow}><Typography variant="h6">{formatMessage({ id: 'dashboard.impersonation.invite-title' })}</Typography><Chip size="small" icon={<Timer />} label={formatMessage({ id: 'dashboard.impersonation.pending' })} className={classes.pendingChip} /></Box>
              <Typography>{formatMessage({ id: 'dashboard.impersonation.requested-by' })}: {request.requestedByName}</Typography>
              {request.reason && <Typography>{formatMessage({ id: 'dashboard.impersonation.reason' })}: {request.reason}</Typography>}
              <Typography color="textSecondary">
                {formatMessage({ id: 'dashboard.impersonation.respond-before' })}: {moment.utc(request.respondBeforeUTC).local().format('lll')}
              </Typography>
            </CardContent>
            <CardActions className={classes.actions}>
              <Button color="primary" variant="contained" startIcon={<CheckCircleOutline />} disabled={!!respondingId} onClick={() => respond(request, true)}>
                {formatMessage({ id: 'dashboard.impersonation.accept' })}
              </Button>
              <Button className={classes.rejectButton} variant="outlined" startIcon={<Block />} disabled={!!respondingId} onClick={() => respond(request, false)}>
                {formatMessage({ id: 'dashboard.impersonation.reject' })}
              </Button>
            </CardActions>
          </React.Fragment>
        ))}
      </Card>
    </Grid>
  );
};
